// csvParser.js — CSV parsing and formatting for the recipient import/export.
//
// The column set is not written out here. It comes from RECIPIENT_CSV_FIELDS
// in recipientFields.js, so the export header, the import parser, the blank
// template are all built from one list and cannot drift apart.

import {
    RECIPIENT_CSV_FIELDS,
    RECIPIENT_CSV_REFERENCE_COLUMNS,
    RECIPIENT_CSV_HEADER_MAP,
    RECIPIENT_CSV_FIELD_BY_COLUMN,
    RECIPIENT_CSV_REFERENCE_COLUMN_NAMES,
} from './recipientFields';

/**
 * Export column order: the reference columns first so a tool reading the file
 * can identify a row before it parses anything else, then every author-settable
 * field in the order the specification lists them.
 */
const EXPORT_COLUMNS = [
    ...RECIPIENT_CSV_REFERENCE_COLUMNS.map((c) => ({ header: c.column, get: c.toCsv })),
    ...RECIPIENT_CSV_FIELDS.map((f) => ({ header: f.column, get: f.toCsv })),
];

/**
 * Write recipients out as CSV.
 *
 * A file produced here can be edited and imported back without changes.
 *
 * @param {Array<Object>} recipients Recipient records. Tags are read from a
 *                                   `tags` array of names, which the caller
 *                                   has to join in — they live in a separate
 *                                   table on the server.
 * @returns {string} RFC 4180 CSV. Every field is quoted.
 */
export function formatUsersCsv(recipients) {
    const header = EXPORT_COLUMNS.map((c) => c.header).join(',');
    const lines = (recipients || []).map((r) =>
        EXPORT_COLUMNS.map((c) => escapeCsvField(c.get(r) ?? '')).join(',')
    );
    return [header, ...lines].join('\n');
}

/**
 * Build a blank import template: the header row, then one filled-in example
 * row an author can overwrite.
 *
 * @returns {string} CSV text.
 */
export function buildRecipientCsvTemplate() {
    const columns = RECIPIENT_CSV_FIELDS.map((f) => f.column);
    const example = RECIPIENT_CSV_FIELDS.map((f) => escapeCsvField(f.example));
    return [columns.join(','), example.join(',')].join('\n');
}

/**
 * Parse a recipients CSV.
 *
 * Columns are matched by header name, so the order does not matter and a file
 * may leave out any optional column entirely. A blank cell is not the same as
 * a missing column — both mean "do not set this", and neither erases a value
 * already on the server.
 *
 * @param {string} csvText
 * @returns {{recipients: Array<Object>, warnings: string[], errors: string[], rowCount: number}}
 *   recipients — payloads ready to POST, one per accepted row. Tag names come
 *                back under `tags` as an array; the caller resolves them to IDs.
 *   errors     — rows that will not be imported, each naming its row number.
 *   warnings   — things the author should see but that do not stop the import.
 *   rowCount   — data rows read, including the ones that failed.
 */
export function parseRecipientsCsv(csvText) {
    const warnings = [];
    const errors = [];

    const rows = parseCsv(stripByteOrderMark(csvText || ''));
    const dataRows = rows.slice(1).filter((row) => row.length > 0 && row.some((c) => c.trim() !== ''));

    if (rows.length === 0 || rows[0].every((c) => c.trim() === '')) {
        return { recipients: [], warnings, errors: ['The file is empty.'], rowCount: 0 };
    }

    // ─── Header ───
    const headerRow = rows[0].map((h) => String(h || '').trim().toLowerCase());
    const columnMap = headerRow.map((h) => {
        if (h === '') return null;
        const canonical = RECIPIENT_CSV_HEADER_MAP.get(h);
        if (!canonical) {
            warnings.push(`Column "${h}" is not a recipient field. It was ignored.`);
            return null;
        }
        return canonical;
    });

    // A column named twice is a real hazard: the second one silently wins and
    // the author never learns which of their two columns was used.
    const seen = new Set();
    for (const col of columnMap) {
        if (!col) continue;
        if (seen.has(col)) {
            errors.push(`Column "${col}" appears more than once. Remove the duplicate and import again.`);
        }
        seen.add(col);
    }

    if (!seen.has('email')) {
        errors.push('The file has no "email" column. Email is the only required field.');
        return { recipients: [], warnings, errors, rowCount: dataRows.length };
    }

    // ─── Rows ───
    const recipients = [];
    let dataRowNumber = 0;

    for (let i = 1; i < rows.length; i++) {
        const row = rows[i];
        if (row.length === 0 || row.every((c) => c.trim() === '')) continue;
        dataRowNumber += 1;
        // The row number quoted to the author is the line in their file, so it
        // matches what their spreadsheet shows.
        const lineNumber = i + 1;

        const payload = {};
        let rowFailed = false;

        for (let c = 0; c < columnMap.length; c++) {
            const column = columnMap[c];
            if (!column) continue;

            const cell = (row[c] ?? '').trim();
            // Blank means "do not set this field". It never clears a value.
            if (cell === '') continue;

            if (RECIPIENT_CSV_REFERENCE_COLUMN_NAMES.has(column)) continue;

            const field = RECIPIENT_CSV_FIELD_BY_COLUMN.get(column);
            const result = field.check(cell);

            if (result.error) {
                errors.push(`Row ${lineNumber}, ${column}: ${result.error}`);
                rowFailed = true;
                continue;
            }
            if (result.warning) {
                warnings.push(`Row ${lineNumber}, ${column}: ${result.warning}`);
            }
            payload[column] = result.value;
        }

        if (!payload.email) {
            // Only report the missing email if the row has not already failed
            // on a bad email value, so one mistake produces one message.
            if (!rowFailed) {
                errors.push(`Row ${lineNumber}: email is blank. Every row needs an email address.`);
            }
            continue;
        }
        if (rowFailed) continue;

        recipients.push(payload);
    }

    // ─── Duplicates inside the file itself ───
    // The duplicate key on the server is email plus name, so that is what is
    // compared here. Two rows with the same email but different names are two
    // different people and are left alone.
    const identities = new Map();
    for (let n = 0; n < recipients.length; n++) {
        const key = `${recipients[n].email} ${(recipients[n].name || '').trim().toLowerCase()}`;
        if (identities.has(key)) {
            warnings.push(
                `"${recipients[n].email}" appears more than once with the same name. The last row in the file wins.`
            );
        }
        identities.set(key, n);
    }

    return { recipients, warnings, errors, rowCount: dataRowNumber };
}

/**
 * Legacy three-column parser, kept only so an old "email,name,tags" file still
 * imports. New callers should use parseRecipientsCsv, which reads every field.
 *
 * @deprecated
 */
export function parseUsersCsv(csvText) {
    return parseRecipientsCsv(csvText).recipients;
}

/** Remove a leading byte order mark, which Excel writes and which would otherwise corrupt the first header. */
function stripByteOrderMark(text) {
    return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
}

/**
 * Escape a CSV field per RFC 4180.
 * Always wraps in quotes and escapes internal quotes.
 * @param {string} value
 * @returns {string}
 */
function escapeCsvField(value) {
    const escaped = String(value).replace(/"/g, '""');
    return `"${escaped}"`;
}

/**
 * Minimal RFC 4180 CSV parser.
 * Handles quoted fields, commas, and CRLF/LF newlines.
 * @param {string} text
 * @returns {string[][]}
 */
function parseCsv(text) {
    const rows = [];
    let row = [];
    let field = "";
    let i = 0;
    let inQuotes = false;

    while (i < text.length) {
        const char = text[i];

        if (inQuotes) {
            if (char === '"') {
                const nextChar = text[i + 1];
                if (nextChar === '"') {
                    field += '"';
                    i += 2;
                    continue;
                } else {
                    inQuotes = false;
                    i += 1;
                    continue;
                }
            } else {
                field += char;
                i += 1;
                continue;
            }
        } else {
            if (char === '"') {
                inQuotes = true;
                i += 1;
                continue;
            }
            if (char === ",") {
                row.push(field);
                field = "";
                i += 1;
                continue;
            }
            if (char === "\r") {
                // Handle CRLF
                if (text[i + 1] === "\n") i += 1;
                row.push(field);
                rows.push(row);
                row = [];
                field = "";
                i += 1;
                continue;
            }
            if (char === "\n") {
                row.push(field);
                rows.push(row);
                row = [];
                field = "";
                i += 1;
                continue;
            }
            field += char;
            i += 1;
        }
    }

    // Push last field/row if needed
    row.push(field);
    rows.push(row);

    return rows;
}
